import {
  ArrowLeftRightIcon,
  CalendarIcon,
  FileSearch2Icon,
  Gauge,
  Grip,
  MessageSquareIcon,
  Palette,
  PhoneCall,
  Settings2,
  Volume2,
  ZapIcon,
} from "lucide-react";

import {
  AssistantAiModel,
  AssistantPatienceLevelModel,
  AssistantVoiceGenderModel,
  AssistantVoiceLanguageModel,
  AssistantVoiceSynthesizerModel,
  AssistantWidgetVoiceModel,
  AssistantTranscriberProviderModel,
  AssistantTypeModel,
  AssistantSpeechRecognitionLevelModel,
} from "@/api/models";
import {ReactComponent as SynthflowIcon} from "@/assets/icons/synthflow-ai-model.svg";
import {GPTIcon, GaugeLow, GaugeMedium, GaugeMax} from "@/components/icons";
import {
  BrazilianFlag,
  BulgarianFlag,
  CzechFlag,
  DanishFlag,
  DutchFlag,
  EnglishFlag,
  FinnishFlag,
  FrenchFlag,
  GermanFlag,
  GreekFlag,
  HindiFlag,
  HungarianFlag,
  IndonesianFlag,
  ItalianFlag,
  JapaneseFlag,
  KoreanFlag,
  MalayFlag,
  MandarinFlag,
  MultilingualFlag,
  NorwegianFlag,
  PolishFlag,
  RomanianFlag,
  RussianFlag,
  SlovakFlag,
  SpanishFlag,
  SwedishFlag,
  ThaiFlag,
  TurkishFlag,
  UkrainianFlag,
  VietnameseFlag,
} from "@/components/icons/languageFlags";

export const ASSISTANT_AI_MODELS_MAP = {
  [AssistantAiModel.SYNTHFLOW]: {
    name: "Synthflow",
    provider: "Synthflow",
    description: "Optimized for real-time AI conversations",
    icon: <SynthflowIcon className="rounded-md" />,
    tag: null,
  },
  [AssistantAiModel.GPT_4O]: {
    name: "GPT-4o",
    provider: "OpenAI",
    description: "High quality, context understanding and speed",
    icon: <GPTIcon className="rounded-md" />,
    tag: "Recommended",
  },
  [AssistantAiModel.GPT_41]: {
    name: "GPT-4.1",
    provider: "OpenAI",
    description:
      "Former Flagship GPT model for complex tasks, slightly slower performance",
    icon: <GPTIcon className="rounded-md" />,
    tag: null,
  },
  [AssistantAiModel.GPT_41_MINI]: {
    name: "GPT-4.1-Mini",
    provider: "OpenAI",
    description: "New model with similar performance to GPT-4.1",
    icon: <GPTIcon className="rounded-md" />,
    tag: null,
  },
  [AssistantAiModel.GPT_5_CHAT]: {
    name: "GPT-5-Chat",
    provider: "OpenAI",
    description:
      "Full-featured flagship version of GPT-5 for the most advanced conversations.",
    icon: <GPTIcon className="rounded-md" />,
    tag: "Beta",
  },
} as const;

export const ASSISTANT_WIDGET_AI_MODELS_MAP = {
  [AssistantAiModel.SYNTHFLOW]: {
    name: "Synthflow",
    provider: "Synthflow",
    description: "Optimized for real-time AI conversations",
    icon: <SynthflowIcon className="max-w-8 max-h-8 rounded-md" />,
    tag: null,
  },
} as const;

export const ASSISTANT_AI_MODELS = Object.entries(ASSISTANT_AI_MODELS_MAP).map(
  ([value, data]) => ({
    value,
    ...data,
  }),
);

export const ASSISTANT_WIDGET_AI_MODELS = Object.entries(
  ASSISTANT_WIDGET_AI_MODELS_MAP,
).map(([value, data]) => ({
  value,
  ...data,
}));

export const ASSISTANT_PATIENCE_LEVELS = [
  {
    title: "Low",
    value: AssistantPatienceLevelModel.LOW,
    description: "~1 sec",
  },
  {
    title: "Medium",
    value: AssistantPatienceLevelModel.MEDIUM,
    description: "~3 sec",
  },
  {
    title: "High",
    value: AssistantPatienceLevelModel.HIGH,
    description: "~5 sec",
  },
];

export const ASSISTANT_SPEECH_RECOGNITION_LEVELS = [
  {
    title: "Faster",
    value: AssistantSpeechRecognitionLevelModel.FASTER,
    description: "Lower quality, suitable for most use cases",
  },
  {
    title: "High Accuracy",
    value: AssistantSpeechRecognitionLevelModel.HIGH_ACCURACY,
    description: "Slower, for high accuracy use cases",
  },
];

export const ASSISTANT_OPTIMIZE_STREAMING_LATENCY = [
  {
    title: "None",
    value: 0,
    description: "No latency optimization, highest quality.",
  },
  {
    title: "Low",
    value: 1,
    description: "Slight latency optimization, minimal quality reduction.",
  },
  {
    title: "Medium",
    value: 2,
    description: "Moderate latency optimization, noticeable quality trade-off.",
  },
  {
    title: "High",
    value: 3,
    description: "Significant latency optimization, reduced voice quality.",
  },
  {
    title: "Max",
    value: 4,
    description:
      "Maximum latency optimization with Text Normalizer off, fastest response but potential for mispronunciations.",
  },
];

export const ASSISTANT_OPTIMIZE_STREAMING_LATENCY_ICONS: Record<
  number,
  React.ElementType
> = {
  0: GaugeLow,
  1: GaugeLow,
  2: GaugeMedium,
  3: Gauge,
  4: GaugeMax,
};

export const ASSISTANT_SYNTHESIZER_STYLE = {
  MIN: 0,
  MAX: 1,
  STEP: 0.01,
  MARKS: [
    {
      value: 0,
      label: "Minimal",
    },
    {
      value: 0.25,
      label: "Subtle",
    },
    {
      value: 0.5,
      label: "Moderate",
    },
    {
      value: 0.75,
      label: "Enhanced",
    },
    {
      value: 1,
      label: "Exaggerated",
    },
  ],
};

export const ASSISTANT_SYNTHESIZER_STABILITY = {
  MIN: 0,
  MAX: 1,
  STEP: 0.01,
  MARKS: [
    {
      value: 0,
      label: "Expressive",
    },
    {
      value: 0.25,
      label: "Dynamic",
    },
    {
      value: 0.5,
      label: "Balanced",
    },
    {
      value: 0.75,
      label: "Predictable",
    },
    {
      value: 1,
      label: "Stable",
    },
  ],
};

export const ASSISTANT_SYNTHESIZER_SIMILARITY = {
  MIN: 0,
  MAX: 1,
  STEP: 0.01,
  MARKS: [
    {
      value: 0,
      label: "Low",
    },
    {
      value: 0.25,
      label: "Moderate",
    },
    {
      value: 0.5,
      label: "Balanced",
    },
    {
      value: 0.75,
      label: "High",
    },
    {
      value: 1,
      label: "Maximum",
    },
  ],
};

export const ASSISTANT_VOICE_SPEED = {
  MIN: 0.7,
  MAX: 1.2,
  STEP: 0.05,
  MARKS: [
    {
      value: 0.7,
      label: "Slow",
    },
    {
      value: 0.95,
      label: "Conversational",
    },
    {
      value: 1.2,
      label: "Fast",
    },
  ],
};

export const DEFAULT_SUPPRESSION_LEVEL = 50;

export const ASSISTANT_VOICE_VOLUME = {
  MIN: 0,
  MAX: 1,
  STEP: 0.05,
  MARKS: [
    {
      value: 0,
      label: "0%",
    },
    {
      value: 0.25,
      label: "25%",
    },
    {
      value: 0.5,
      label: "50%",
    },
    {
      value: 0.75,
      label: "75%",
    },
    {
      value: 1,
      label: "100%",
    },
  ],
};

export const ASSISTANT_MIN_WORDS_TO_INTERRUPT = {
  MIN: 0,
  MAX: 5,
  STEP: 1,
  MARKS: [
    {
      value: 0,
      label: "Off",
    },
    {
      value: 1,
      label: "1",
    },
    {
      value: 2,
      label: "2",
    },
    {
      value: 3,
      label: "3",
    },
    {
      value: 4,
      label: "4",
    },
    {
      value: 5,
      label: "5",
    },
  ],
};

export const ASSISTANT_INITIAL_PAUSE = {
  MIN: 0,
  MAX: 10,
  STEP: 1,
  MARKS: [
    {
      value: 0,
      label: "0s",
    },
    {
      value: 2,
      label: "2s",
    },
    {
      value: 4,
      label: "4s",
    },
    {
      value: 6,
      label: "6s",
    },
    {
      value: 8,
      label: "8s",
    },
    {
      value: 10,
      label: "10s",
    },
  ],
};

export const ASSISTANT_RING_DURATION = {
  MIN: 0,
  MAX: 30,
  STEP: 1,
  MARKS: [
    {
      value: 0,
      label: "0s",
    },
    {
      value: 5,
      label: "5s",
    },
    {
      value: 10,
      label: "10s",
    },
    {
      value: 15,
      label: "15s",
    },
    {
      value: 20,
      label: "20s",
    },
    {
      value: 25,
      label: "25s",
    },
    {
      value: 30,
      label: "30s",
    },
  ],
};

export const ASSISTANT_ALLOWED_IDLE_TIME = {
  MIN: 0,
  MAX: 100,
  STEP: 1,
  MARKS: [
    {
      value: 0,
      label: "0s",
    },
    {
      value: 25,
      label: "25s",
    },
    {
      value: 50,
      label: "50s",
    },
    {
      value: 75,
      label: "75s",
    },
    {
      value: 100,
      label: "100s",
    },
  ],
};

export const ASSISTANT_MAX_DURATION = {
  MIN: 0,
  MAX: 30, // 30 minutes = 1800 seconds
  STEP: 1,
  MARKS: [
    {
      value: 0,
      label: "0m",
    },
    {
      value: 5,
      label: "5m",
    },
    {
      value: 10,
      label: "10m",
    },
    {
      value: 15,
      label: "15m",
    },
    {
      value: 20,
      label: "20m",
    },
    {
      value: 25,
      label: "25m",
    },
    {
      value: 30,
      label: "30m",
    },
  ],
};

export const ASSISTANT_BACKGROUND_NOISE = {
  MIN: 1,
  MAX: 50,
  STEP: 1,
  MARKS: [
    {value: 1, label: "1"},
    {value: 10, label: "10"},
    {value: 20, label: "20"},
    {value: 30, label: "30"},
    {value: 40, label: "40"},
    {value: 50, label: "50"},
  ],
};
export const ASSISTANT_REMINDER_AFTER_IDLE = {
  MIN: 0,
  MAX: 20,
  STEP: 1,
  MARKS: [
    {
      value: 0,
      label: "0s",
    },
    {
      value: 5,
      label: "5s",
    },
    {
      value: 10,
      label: "10s",
    },
    {
      value: 15,
      label: "15s",
    },
    {
      value: 20,
      label: "20s",
    },
  ],
};

export const ASSISTANT_VOICE_LANGUAGES = [
  {
    value: AssistantVoiceLanguageModel.ENGLISH,
    label: "English",
    icon: EnglishFlag,
  },
  {
    value: AssistantVoiceLanguageModel.PORTUGUESE_BR,
    label: "Portuguese (Brazil)",
    icon: BrazilianFlag,
  },
  {
    value: AssistantVoiceLanguageModel.SPANISH,
    label: "Spanish",
    icon: SpanishFlag,
  },
  {
    value: AssistantVoiceLanguageModel.FRENCH,
    label: "French",
    icon: FrenchFlag,
  },
  {
    value: AssistantVoiceLanguageModel.GERMAN,
    label: "German",
    icon: GermanFlag,
  },
  {
    value: AssistantVoiceLanguageModel.ITALIAN,
    label: "Italian",
    icon: ItalianFlag,
  },
  {value: AssistantVoiceLanguageModel.DUTCH, label: "Dutch", icon: DutchFlag},
  {
    value: AssistantVoiceLanguageModel.MULTILINGUAL,
    label: "Multilingual",
    icon: MultilingualFlag,
  },
  {
    value: AssistantVoiceLanguageModel.HINDI,
    label: "Hindi (Beta)",
    icon: HindiFlag,
  },
  {
    value: AssistantVoiceLanguageModel.MANDARIN,
    label: "Mandarin (Beta)",
    icon: MandarinFlag,
  },
  {
    value: AssistantVoiceLanguageModel.NORWEGIAN,
    label: "Norwegian (Beta)",
    icon: NorwegianFlag,
  },
  {
    value: AssistantVoiceLanguageModel.SWEDISH,
    label: "Swedish (Beta)",
    icon: SwedishFlag,
  },
  {
    value: AssistantVoiceLanguageModel.POLISH,
    label: "Polish (Beta)",
    icon: PolishFlag,
  },
  {
    value: AssistantVoiceLanguageModel.DANISH,
    label: "Danish (Beta)",
    icon: DanishFlag,
  },
  {
    value: AssistantVoiceLanguageModel.DANISH_DK,
    label: "Danish (Denmark)",
    icon: DanishFlag,
  },
  {
    value: AssistantVoiceLanguageModel.BULGARIAN,
    label: "Bulgarian (Beta)",
    icon: BulgarianFlag,
  },
  {
    value: AssistantVoiceLanguageModel.CZECH,
    label: "Czech (Beta)",
    icon: CzechFlag,
  },
  {
    value: AssistantVoiceLanguageModel.FINNISH,
    label: "Finnish (Beta)",
    icon: FinnishFlag,
  },
  {
    value: AssistantVoiceLanguageModel.GREEK,
    label: "Greek (Beta)",
    icon: GreekFlag,
  },
  {
    value: AssistantVoiceLanguageModel.HUNGARIAN,
    label: "Hungarian (Beta)",
    icon: HungarianFlag,
  },
  {
    value: AssistantVoiceLanguageModel.INDONESIAN,
    label: "Indonesian (Beta)",
    icon: IndonesianFlag,
  },
  {
    value: AssistantVoiceLanguageModel.JAPANESE,
    label: "Japanese (Beta)",
    icon: JapaneseFlag,
  },
  {
    value: AssistantVoiceLanguageModel.KOREAN,
    label: "Korean (Beta)",
    icon: KoreanFlag,
  },
  {
    value: AssistantVoiceLanguageModel.MALAY,
    label: "Malay (Beta)",
    icon: MalayFlag,
  },
  {
    value: AssistantVoiceLanguageModel.ROMANIAN,
    label: "Romanian (Beta)",
    icon: RomanianFlag,
  },
  {
    value: AssistantVoiceLanguageModel.RUSSIAN,
    label: "Russian (Beta)",
    icon: RussianFlag,
  },
  {
    value: AssistantVoiceLanguageModel.SLOVAK,
    label: "Slovak (Beta)",
    icon: SlovakFlag,
  },
  {
    value: AssistantVoiceLanguageModel.THAI,
    label: "Thai (Beta)",
    icon: ThaiFlag,
  },
  {
    value: AssistantVoiceLanguageModel.TURKISH,
    label: "Turkish (Beta)",
    icon: TurkishFlag,
  },
  {
    value: AssistantVoiceLanguageModel.UKRAINIAN,
    label: "Ukrainian (Beta)",
    icon: UkrainianFlag,
  },
  {
    value: AssistantVoiceLanguageModel.VIETNAMESE,
    label: "Vietnamese (Beta)",
    icon: VietnameseFlag,
  },
];

export const ASSISTANT_VOICE_LANGUAGES_MAP = ASSISTANT_VOICE_LANGUAGES.reduce(
  (acc, lang) => ({
    ...acc,
    [lang.value]: lang,
  }),
  {} as Record<
    AssistantVoiceLanguageModel,
    (typeof ASSISTANT_VOICE_LANGUAGES)[0]
  >,
);

export const VOICE_SYNTHESIZER_MODELS_MAP = {
  [AssistantVoiceSynthesizerModel.TURBO_V2]: {
    label: "ElevenLabs Turbo v2 (english only)",
    description: "Suited for tasks demanding extremely low latency.",
    tag: "English only",
  },
  [AssistantVoiceSynthesizerModel.TURBO_V2_5]: {
    label: "ElevenLabs Turbo v2.5 (recommended for non-English)",
    description:
      "Low latency model in 32 languages. Best for use cases where speed matters and you need non-English languages.",
    tag: "Recommended for non-English",
  },
  [AssistantVoiceSynthesizerModel.FLASH_V2]: {
    label: "ElevenLabs Flash v2 (english only)",
    description: "Ultra-fast model optimized for real-time use (~75ms)",
    tag: "English only",
  },
  [AssistantVoiceSynthesizerModel.FLASH_V2_5]: {
    label: "ElevenLabs Flash v2.5 (recommended for non-english)",
    description: "Ultra-fast model optimized for real-time use (~75ms)",
    tag: "Recommended for non-English",
  },
  [AssistantVoiceSynthesizerModel.MULTILINGUAL_V2]: {
    label: "ElevenLabs Multilingual v2",
    description:
      "Multilingual speech synthesis model, able to generate life-like speech in 29 languages.",
  },
} as const;

export const ASSISTANT_VOICE_SYNTHESIZER_MODELS = Object.entries(
  VOICE_SYNTHESIZER_MODELS_MAP,
).map(([value, data]) => ({
  value,
  ...data,
}));

export const MAX_ASSISTANT_NAME_LENGTH = 100;

// Voice Synthesizer Limits
export const ASSISTANT_SYNTHESIZER_RANGE = {
  MIN: 0,
  MAX: 1,
} as const;

export const ASSISTANT_TEMPERATURE_RANGE = {
  MIN: 0.6,
  MAX: 1.2,
} as const;

export const ASSISTANT_VOICE_STREAMING_LATENCY = {
  MIN: 0,
  MAX: 4,
  ALLOWED_VALUES: [0, 1, 2, 3, 4],
} as const;

// Duration and Time Limits
export const ASSISTANT_INITIAL_PAUSE_SECONDS = {
  MIN: 0,
  MAX: 10,
} as const;

export const ASSISTANT_RING_DURATION_SECONDS = {
  MIN: 0,
  MAX: 30,
} as const;

export const ASSISTANT_CALL_DURATION_SECONDS = {
  MIN: 0,
  MAX: 1200, // 20 minutes
} as const;

export const ASSISTANT_IDLE_TIME_SECONDS = {
  MIN: 0,
  MAX: 100,
} as const;

export const ASSISTANT_IDLE_REMINDER_SECONDS = {
  MIN: 0,
  MAX: 20,
} as const;

export const ASSISTANT_VOICE_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

export const ASSISTANT_ACTION_TYPES = {
  transfer: {
    title: "Call Transfer",
    isBeta: false,
    Icon: ArrowLeftRightIcon,
    searchPlaceholder: "Search by number",
    description: "Transfer the call live to a human",
  },
  custom: {
    title: "Custom Action",
    isBeta: false,
    Icon: ZapIcon,
    description: "Build a custom tool to interact with the real world mid-call",
  },
  booking: {
    title: "Real Time Booking",
    isBeta: false,
    Icon: CalendarIcon,
    searchPlaceholder: "Search by title",
    description: "Schedule live appointments during the call",
  },
  sms: {
    title: "Send SMS",
    isBeta: false,
    description: "Send a custom SMS after the call",
    Icon: MessageSquareIcon,
    searchPlaceholder: "Search by SMS content",
  },
  info: {
    title: "Information Extractor",
    isBeta: false,
    description: "Extracts information from the call",
    Icon: FileSearch2Icon,
    searchPlaceholder: "Search by key",
  },
  ivr: {
    title: "IVR",
    isBeta: false,
    Icon: Grip,
    description: "Automated voice menues to route calls and trigger actions",
  },
  eval: {
    title: "Custom Evaluation",
    isBeta: false,
    Icon: FileSearch2Icon,
    description: "Create and manage evaluation templates",
  },
} as const;

export enum AssistantConfigureTabKey {
  GENERAL = "general",
  VOICE = "voice",
  CALL = "call",
  APPEARANCE = "appearance",
}

export const ASSISTANT_CONFIGURE_TABS = [
  {
    value: AssistantConfigureTabKey.GENERAL,
    label: "General",
    icon: Settings2,
    key: AssistantConfigureTabKey.GENERAL,
  },
  {
    value: AssistantConfigureTabKey.VOICE,
    label: "Voice",
    icon: Volume2,
    key: AssistantConfigureTabKey.VOICE,
  },
  {
    value: AssistantConfigureTabKey.CALL,
    label: "Call Configuration",
    icon: PhoneCall,
    key: AssistantConfigureTabKey.CALL,
  },
  {
    value: AssistantConfigureTabKey.APPEARANCE,
    label: "Appearance",
    icon: Palette,
    key: AssistantConfigureTabKey.APPEARANCE,
    isNew: true,
  },
];

export enum AssistantDeploymentTabKey {
  TELEPHONE = "telephone",
  WHATSAPP_CALLS = "whatsapp_calls",
}

export const ASSISTANT_DEPLOYMENT_TABS = [
  {
    value: AssistantDeploymentTabKey.TELEPHONE,
    label: "Telephone",
    key: AssistantDeploymentTabKey.TELEPHONE,
  },
  {
    value: AssistantDeploymentTabKey.WHATSAPP_CALLS,
    label: "Whatsapp",
    key: AssistantDeploymentTabKey.WHATSAPP_CALLS,
  },
];

export const ASSISTANT_VOICE_GENDER_MAP = {
  [AssistantVoiceGenderModel.MALE]: "Male",
  [AssistantVoiceGenderModel.FEMALE]: "Female",
};

export const ASSISTANT_WIDGET_VOICES = [
  {
    value: AssistantWidgetVoiceModel.ALLOY,
    label: "Alloy",
  },
  {
    value: AssistantWidgetVoiceModel.ECHO,
    label: "Echo",
  },
  {
    value: AssistantWidgetVoiceModel.SHIMMER,
    label: "Shimmer",
  },
];
export const ASSISTANT_TRANSCRIBER_PROVIDER_MAP = {
  [AssistantTranscriberProviderModel.DEEPGRAM_NOVA_2]: {
    label: "Deepgram Nova 2",
  },
  [AssistantTranscriberProviderModel.DEEPGRAM_NOVA_3]: {
    label: "Deepgram Nova 3 (english and multilang only)",
  },
} as const;

export const ASSISTANT_TRANSCRIBER_PROVIDERS = Object.entries(
  ASSISTANT_TRANSCRIBER_PROVIDER_MAP,
).map(([value, data]) => ({
  value,
  ...data,
}));

export const ASSISTANT_TYPES = [
  {text: "Inbound", value: AssistantTypeModel.INBOUND},
  {text: "Outbound", value: AssistantTypeModel.OUTBOUND},
  {text: "Widget", value: AssistantTypeModel.WIDGET},
] as const;
